import {pushEventToFacebook} from "./growtypeFacebookBridge";

// Optional debug
const GTAC_DEBUG = false;

// Initialize global queue
window._gtacQueue = window._gtacQueue || [];
window._gtacLocalCommerceQueue = window._gtacLocalCommerceQueue || [];

const LOCAL_COMMERCE_EVENTS = [
    'growtype_analytics_growtype_wc_offer_impression',
    'growtype_analytics_growtype_wc_checkout_cta_click',
    'growtype_analytics_growtype_wc_confirmation_shown',
    'growtype_analytics_growtype_wc_confirmation_confirmed',
    'growtype_analytics_growtype_wc_confirmation_cancelled',
    'growtype_analytics_growtype_wc_begin_checkout',
    'growtype_analytics_growtype_wc_begin_payment',
    'growtype_analytics_growtype_wc_payment_form_loaded',
    'growtype_analytics_growtype_wc_payment_form_error',
    'growtype_analytics_growtype_wc_provider_redirect',
    'growtype_analytics_growtype_wc_provider_return',
    'growtype_analytics_growtype_wc_provider_cancelled',
    'growtype_analytics_growtype_wc_checkout_form_submit',
];

function getPrivacySafeCommerceMetadata(data) {
    const ecommerce = data?.ecommerce && typeof data.ecommerce === 'object' ? data.ecommerce : {};
    const metadata = {};
    const value = data?.value ?? ecommerce.value;
    const currency = data?.currency ?? ecommerce.currency;
    const paymentMethod = data?.payment_method;
    const checkoutAttemptId = data?.checkout_attempt_id;
    const productId = data?.product_id;
    const outcome = data?.outcome;
    const errorCode = data?.error_code;

    if ((typeof value === 'number' || typeof value === 'string') && String(value).length <= 32) {
        metadata.value = value;
    }
    if (typeof currency === 'string' && /^[A-Z]{3}$/i.test(currency)) {
        metadata.currency = currency.toUpperCase();
    }
    if (typeof paymentMethod === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(paymentMethod)) {
        metadata.payment_method = paymentMethod;
    }
    if (
        typeof checkoutAttemptId === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(checkoutAttemptId)
    ) {
        metadata.checkout_attempt_id = checkoutAttemptId.toLowerCase();
    }
    if ((typeof productId === 'string' || typeof productId === 'number') && /^[0-9]{1,32}$/.test(String(productId))) {
        metadata.product_id = String(productId);
    }
    if (typeof outcome === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(outcome)) {
        metadata.outcome = outcome;
    }
    if (typeof errorCode === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(errorCode)) {
        metadata.error_code = errorCode;
    }

    return metadata;
}

function persistLocalCommerceEvent(event, data) {
    const metadata = getPrivacySafeCommerceMetadata(data);
    if (!metadata.checkout_attempt_id) return;

    const item = {
        event,
        payload: {
            object_id: '',
            object_type: 'commerce',
            metadata,
        },
    };

    if (typeof window.gaTrack === 'function') {
        window.gaTrack(item.event, item.payload);
        return;
    }

    // Checkout UI can initialize before the local tracker bundle. Retain only
    // the already-sanitized commerce payload until gaTrack becomes available.
    if (window._gtacLocalCommerceQueue.length >= 100) {
        window._gtacLocalCommerceQueue.shift();
    }
    window._gtacLocalCommerceQueue.push(item);
}

function flushLocalCommerceQueue() {
    if (typeof window.gaTrack !== 'function') return;

    while (window._gtacLocalCommerceQueue.length > 0) {
        const item = window._gtacLocalCommerceQueue.shift();
        window.gaTrack(item.event, item.payload);
    }
}

// Unified analytics function
window.growtypeAnalyticsCapture = function(event, data = {}, options = {}) {
    if (!event) return;
    if (!data) data = {};

    // Push event to queue
    if (window._gtacQueue.length >= 100) window._gtacQueue.shift();
    window._gtacQueue.push({ event, data, queuedAt: Date.now(), gtmDelivered: false, posthogDelivered: false });

    // Store only the commerce milestones and fields required by the local
    // aggregate report. Never copy customer, order, transaction, or item data.
    if (LOCAL_COMMERCE_EVENTS.indexOf(event) !== -1) {
        persistLocalCommerceEvent(event, data);
    }

    pushEventToFacebook(event, data, options);

    if (GTAC_DEBUG) console.log('Event queued:', event, data);
};

// Flush queued events continuously until GTM and PostHog are ready
(function flushGtacQueue() {
    flushLocalCommerceQueue();

    if (!window._gtacQueue || window._gtacQueue.length === 0) {
        setTimeout(flushGtacQueue, 100);
        return;
    }

    const gtmReady = Array.isArray(window.dataLayer);
    const posthogReady = typeof window.posthog?.capture === 'function';

    window._gtacQueue = window._gtacQueue.filter(item => {
        if (gtmReady && !item.gtmDelivered) {
            try {
                window.dataLayer.push({ event: item.event, ...item.data });
                item.gtmDelivered = true;
            } catch (e) { if (GTAC_DEBUG) console.error(e); }
        }
        if (posthogReady && !item.posthogDelivered) {
            try {
                // No contact details, chat content or arbitrary quiz answers are
                // needed for conversion counts. The SDK also sanitizes URLs.
                const safe = {};
                ['environment', 'milestone', '$insert_id'].forEach(key => {
                    if (typeof item.data[key] === 'string') safe[key] = item.data[key];
                });
                window.posthog.capture(item.event, safe);
                item.posthogDelivered = true;
            } catch (e) { if (GTAC_DEBUG) console.error(e); }
        }
        return !(item.gtmDelivered && item.posthogDelivered) && Date.now() - item.queuedAt < 60000;
    });

    // Retry in 100ms
    setTimeout(flushGtacQueue, 100);
})();
